import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [settings, setSettings] = useState(null);
  // username -> display name. Glitches/logs store the login username; the
  // app shows the person's Name wherever it says who did something.
  const [names, setNames] = useState({});
  const reloadNames = useCallback(() => {
    api.get("/users/names").then(({ data }) => setNames(data || {})).catch(() => {});
  }, []);
  const nameOf = useCallback((u) => (u && names[u]) || u || "", [names]);

  const loadMe = async () => {
    const token = localStorage.getItem("gm_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("gm_token");
      setUser(false);
    }
  };

  const loadPublicSettings = async () => {
    try {
      const { data } = await api.get("/settings/public");
      setSettings(data);
    } catch (e) {
      setSettings({ general: { app_name: "Glitch Manager" } });
    }
  };

  useEffect(() => { if (user) reloadNames(); }, [user, reloadNames]);

  useEffect(() => {
    loadMe();
    loadPublicSettings();
  }, []);

  const login = (token, u) => {
    localStorage.setItem("gm_token", token);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem("gm_token");
    setUser(false);
    // The app uses HashRouter (routes are "#/login", etc. — see App.js and
    // the 401 interceptor in lib/api.js, which already does this). A bare
    // "/login" path 404s on most static hosts instead of landing here.
    window.location.hash = "#/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, settings, names, nameOf, reloadNames }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
